/**
 * Connected-workflow program (2026-09-10) — Material & Volume Estimate
 * panel. Attaches to a measurement (area for asphalt/concrete/base/
 * lime, distance/polyline for trench/pipe-bedding) and lets an
 * estimator layer explicit, unambiguous assumptions on top of it
 * without ever mutating the measured value itself.
 *
 * Wave 5 (2026-09-11, HCSS Head-to-Head) — replaced the free-text
 * "material_kind" input with a field-first preset picker
 * (Asphalt / Concrete / Flex Base / Trench / Pipe Bedding / Lime).
 * Every preset is pure UI/label metadata fetched from
 * GET /material-estimates/presets — it wraps the SAME canonical
 * services/material_estimate_calc.py engine; no preset invents a
 * density/rate/compaction value. See backend/routers/material_estimates.py.
 */
import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, AlertTriangle, Truck, Pencil, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";

const THICKNESS_UNITS = ["in", "ft", "cm", "mm", "m"];
const DENSITY_UNITS = [
  { v: "lb_per_cf", l: "lb / cf" },
  { v: "ton_per_cy", l: "ton / cy" },
];
const RATE_UNITS = [
  { v: "lb_per_sy", l: "lb / sy" },
  { v: "lb_per_sf", l: "lb / sf" },
  { v: "gal_per_sy", l: "gal / sy" },
  { v: "gal_per_sf", l: "gal / sf" },
];
const TRUCK_UNITS = [
  { v: "cy", l: "cy (volume)" },
  { v: "cf", l: "cf (volume)" },
  { v: "ton", l: "ton (mass)" },
  { v: "lb", l: "lb (mass)" },
];

function fmt(n, d = 2) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
}

/** shape="area" needs an area measurement; shape="linear" needs a
 * length (distance/polyline) measurement; shape="rate" needs an area
 * measurement. A preset is only offered if the attached measurement's
 * own kind can actually feed it. */
function presetFitsMeasurementKind(preset, measurementKind) {
  if (preset.shape === "linear") return measurementKind === "distance" || measurementKind === "polyline";
  return measurementKind === "area";
}

function PresetPicker({ presets, measurementKind, onPick }) {
  const usable = presets.filter((p) => presetFitsMeasurementKind(p, measurementKind));
  if (!usable.length) {
    return (
      <div className="text-[10px] text-[var(--fop-text-3)] font-mono" data-testid="material-estimate-no-presets">
        No material preset applies to this measurement type.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid="material-estimate-preset-picker">
      {usable.map((p) => (
        <button
          key={p.id}
          onClick={() => onPick(p)}
          className="px-2 py-2 rounded border border-[var(--fop-border)] bg-[var(--fop-surface-1)] text-left hover:border-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] transition-colors"
          data-testid={`material-estimate-preset-${p.id}`}
        >
          <div className="text-[11px] font-semibold text-[var(--fop-text)]">{p.label}</div>
          <div className="text-[9px] text-[var(--fop-text-3)] font-mono mt-0.5">
            {p.shape === "linear" ? "length × width × depth" : p.shape === "rate" ? "area × application rate" : "area × thickness"}
          </div>
        </button>
      ))}
    </div>
  );
}

/** Shared shape-specific field set used by both create and edit forms.
 * `preset` is null while editing an existing estimate (shape/material_kind
 * are immutable after creation) — in that case field labels fall back to
 * generic text driven off `shape` alone. */
function ShapeFields({ shape, preset, values, setValues, testidPrefix }) {
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));
  const wasteApplicable = preset ? preset.waste_applicable : true;
  const compactionApplicable = preset ? preset.compaction_applicable : shape === "area";
  const truckApplicable = preset ? preset.truck_applicable : shape !== "rate";
  const densityApplicable = preset ? !!preset.fields?.density : shape === "area";

  return (
    <>
      {shape === "area" && (
        <div className="flex items-center gap-1.5">
          <input
            value={values.thicknessValue} onChange={set("thicknessValue")}
            placeholder={preset?.fields?.thickness?.label || "Thickness"} inputMode="decimal"
            className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
            data-testid={`${testidPrefix}-thickness-input`}
          />
          <select value={values.thicknessUnit} onChange={set("thicknessUnit")} className="px-1 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-thickness-unit`}>
            {THICKNESS_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      )}
      {shape === "linear" && (
        <>
          <div className="flex items-center gap-1.5">
            <input
              value={values.widthValue} onChange={set("widthValue")}
              placeholder="Trench/section width" inputMode="decimal"
              className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
              data-testid={`${testidPrefix}-width-input`}
            />
            <select value={values.widthUnit} onChange={set("widthUnit")} className="px-1 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-width-unit`}>
              {THICKNESS_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              value={values.depthValue} onChange={set("depthValue")}
              placeholder={preset?.fields?.thickness?.label || "Depth"} inputMode="decimal"
              className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
              data-testid={`${testidPrefix}-depth-input`}
            />
            <select value={values.depthUnit} onChange={set("depthUnit")} className="px-1 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-depth-unit`}>
              {THICKNESS_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </>
      )}
      {shape === "rate" && (
        <div className="flex items-center gap-1.5">
          <input
            value={values.rateValue} onChange={set("rateValue")}
            placeholder="Application rate" inputMode="decimal"
            className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
            data-testid={`${testidPrefix}-rate-input`}
          />
          <select value={values.rateUnit} onChange={set("rateUnit")} className="px-1 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-rate-unit`}>
            {RATE_UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
          </select>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        {wasteApplicable && (
          <input value={values.wastePct} onChange={set("wastePct")} placeholder="Waste %" inputMode="decimal"
            className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-waste-input`} />
        )}
        {compactionApplicable && (
          <input value={values.compactionPct} onChange={set("compactionPct")} placeholder="Compaction %" inputMode="decimal"
            className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-compaction-input`} />
        )}
      </div>
      {densityApplicable && (
        <div className="flex items-center gap-1.5">
          <input value={values.densityValue} onChange={set("densityValue")} placeholder={preset?.fields?.density?.label || "Density (optional)"} inputMode="decimal"
            className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-density-input`} />
          <select value={values.densityUnit} onChange={set("densityUnit")} className="px-1 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-density-unit`}>
            {DENSITY_UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
          </select>
        </div>
      )}
      {preset?.fields?.density?.placeholder_hint && (
        <div className="text-[9px] text-[var(--fop-text-3)] italic leading-snug">{preset.fields.density.placeholder_hint}</div>
      )}
      {truckApplicable && (
        <div className="flex items-center gap-1.5">
          <input value={values.truckValue} onChange={set("truckValue")} placeholder="Truck capacity (optional)" inputMode="decimal"
            className="flex-1 min-w-0 px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-truck-input`} />
          <select value={values.truckUnit} onChange={set("truckUnit")} className="px-1 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono" data-testid={`${testidPrefix}-truck-unit`}>
            {TRUCK_UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

const DEFAULT_VALUES = {
  thicknessValue: "", thicknessUnit: "in",
  widthValue: "", widthUnit: "ft", depthValue: "", depthUnit: "in",
  rateValue: "", rateUnit: "lb_per_sy",
  wastePct: "", compactionPct: "",
  densityValue: "", densityUnit: "lb_per_cf",
  truckValue: "", truckUnit: "ton",
};

function NewEstimateForm({ measurementId, measurementKind, onCreated, onCancel }) {
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState(null);
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/material-estimates/presets")
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => setPresets([]));
  }, []);

  useEffect(() => {
    if (preset?.fields?.thickness?.default_unit) {
      setValues((v) => ({ ...v, thicknessUnit: preset.fields.thickness.default_unit, depthUnit: preset.fields.thickness.default_unit }));
    }
    if (preset?.fields?.density?.default_unit) {
      setValues((v) => ({ ...v, densityUnit: preset.fields.density.default_unit }));
    }
  }, [preset]);

  const save = async () => {
    if (!preset) return;
    setErr("");
    const payload = {
      measurement_id: measurementId,
      material_kind: preset.material_kind,
      shape: preset.shape,
      waste_pct: values.wastePct ? parseFloat(values.wastePct) : null,
      compaction_pct: values.compactionPct ? parseFloat(values.compactionPct) : null,
      density_value: values.densityValue ? parseFloat(values.densityValue) : null,
      density_unit: values.densityValue ? values.densityUnit : null,
      truck_capacity_value: values.truckValue ? parseFloat(values.truckValue) : null,
      truck_capacity_unit: values.truckValue ? values.truckUnit : null,
    };
    if (preset.shape === "area") {
      const tv = parseFloat(values.thicknessValue);
      if (!tv || tv <= 0) { setErr(`${preset.fields?.thickness?.label || "Thickness"} is required and must be > 0`); return; }
      payload.thickness_value = tv;
      payload.thickness_unit = values.thicknessUnit;
    } else if (preset.shape === "linear") {
      const wv = parseFloat(values.widthValue);
      const dv = parseFloat(values.depthValue);
      if (!wv || wv <= 0 || !dv || dv <= 0) { setErr("Width and depth are both required and must be > 0"); return; }
      payload.width_value = wv;
      payload.width_unit = values.widthUnit;
      payload.depth_value = dv;
      payload.depth_unit = values.depthUnit;
    } else if (preset.shape === "rate") {
      const rv = parseFloat(values.rateValue);
      if (!rv || rv <= 0) { setErr("Application rate is required and must be > 0"); return; }
      payload.rate_value = rv;
      payload.rate_unit = values.rateUnit;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/measurements/${measurementId}/material-estimates`, payload);
      onCreated(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save — check your inputs");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1.5 p-2 rounded bg-[var(--fop-surface-3)] border border-[var(--fop-border)] space-y-1.5" data-testid="material-estimate-form">
      {!preset ? (
        <PresetPicker presets={presets} measurementKind={measurementKind} onPick={setPreset} />
      ) : (
        <>
          <button
            onClick={() => setPreset(null)}
            className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            data-testid="material-estimate-change-preset"
          >
            <ArrowLeft size={10} /> {preset.label}
          </button>
          <ShapeFields shape={preset.shape} preset={preset} values={values} setValues={setValues} testidPrefix="material-estimate" />
        </>
      )}
      {err && (
        <div className="text-[10px] text-[var(--st-escalated)] font-mono" data-testid="material-estimate-error">{err}</div>
      )}
      <div className="flex items-center gap-1.5 pt-0.5">
        {preset && (
          <button onClick={save} disabled={busy} className="flex-1 px-2 py-1 rounded bg-[var(--fop-primary)] text-[var(--fop-on-primary)] text-[10px] font-mono uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-50" data-testid="material-estimate-save">
            {busy ? <Loader2 size={11} className="animate-spin" /> : "Compute & save"}
          </button>
        )}
        <button onClick={onCancel} className="px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]" data-testid="material-estimate-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}

function EditEstimateForm({ est, onSaved, onCancel }) {
  const [values, setValues] = useState({
    ...DEFAULT_VALUES,
    thicknessValue: String(est.thickness_value ?? ""),
    thicknessUnit: est.thickness_unit || "in",
    widthValue: String(est.width_value ?? ""),
    widthUnit: est.width_unit || "ft",
    depthValue: String(est.depth_value ?? ""),
    depthUnit: est.depth_unit || "in",
    rateValue: String(est.rate_value ?? ""),
    rateUnit: est.rate_unit || "lb_per_sy",
    wastePct: est.waste_pct != null ? String(est.waste_pct) : "",
    compactionPct: est.compaction_pct != null ? String(est.compaction_pct) : "",
    densityValue: est.density_value != null ? String(est.density_value) : "",
    densityUnit: est.density_unit || "lb_per_cf",
    truckValue: est.truck_capacity_value != null ? String(est.truck_capacity_value) : "",
    truckUnit: est.truck_capacity_unit || "ton",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const shape = est.shape || "area";

  const save = async () => {
    setErr("");
    const payload = {
      waste_pct: values.wastePct ? parseFloat(values.wastePct) : null,
      compaction_pct: values.compactionPct ? parseFloat(values.compactionPct) : null,
      density_value: values.densityValue ? parseFloat(values.densityValue) : null,
      density_unit: values.densityValue ? values.densityUnit : null,
      truck_capacity_value: values.truckValue ? parseFloat(values.truckValue) : null,
      truck_capacity_unit: values.truckValue ? values.truckUnit : null,
    };
    if (shape === "area") {
      const tv = parseFloat(values.thicknessValue);
      if (!tv || tv <= 0) { setErr("Thickness is required and must be > 0"); return; }
      payload.thickness_value = tv;
      payload.thickness_unit = values.thicknessUnit;
    } else if (shape === "linear") {
      const wv = parseFloat(values.widthValue);
      const dv = parseFloat(values.depthValue);
      if (!wv || wv <= 0 || !dv || dv <= 0) { setErr("Width and depth are both required and must be > 0"); return; }
      payload.width_value = wv;
      payload.width_unit = values.widthUnit;
      payload.depth_value = dv;
      payload.depth_unit = values.depthUnit;
    } else if (shape === "rate") {
      const rv = parseFloat(values.rateValue);
      if (!rv || rv <= 0) { setErr("Application rate is required and must be > 0"); return; }
      payload.rate_value = rv;
      payload.rate_unit = values.rateUnit;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/material-estimates/${est.id}`, payload);
      onSaved(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save — check your inputs");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1.5 p-2 rounded bg-[var(--fop-surface-3)] border border-[var(--fop-border)] space-y-1.5" data-testid={`material-estimate-edit-form-${est.id}`}>
      <ShapeFields shape={shape} preset={null} values={values} setValues={setValues} testidPrefix={`material-estimate-edit-${est.id}`} />
      {err && (
        <div className="text-[10px] text-[var(--st-escalated)] font-mono" data-testid={`material-estimate-edit-error-${est.id}`}>{err}</div>
      )}
      <div className="flex items-center gap-1.5 pt-0.5">
        <button onClick={save} disabled={busy} className="flex-1 px-2 py-1 rounded bg-[var(--fop-primary)] text-[var(--fop-on-primary)] text-[10px] font-mono uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`material-estimate-edit-save-${est.id}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : "Recompute & save"}
        </button>
        <button onClick={onCancel} className="px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]" data-testid={`material-estimate-edit-cancel-${est.id}`}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function EstimateRow({ est, onDeleted, onUpdated }) {
  const c = est.computed;
  const [editing, setEditing] = useState(false);
  const del = async () => {
    if (!window.confirm("Delete this material estimate?")) return;
    try {
      await api.delete(`/material-estimates/${est.id}`);
      onDeleted(est.id);
    } catch {
      confirm.saveFailed("Couldn't delete estimate");
    }
  };
  return (
    <div className="p-1.5 rounded bg-[var(--fop-surface-2)] border border-[var(--fop-border)] text-[10px] font-mono" data-testid={`material-estimate-row-${est.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[var(--fop-text)] font-semibold truncate" data-testid={`material-estimate-kind-${est.id}`}>{est.material_kind}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing((v) => !v)} data-testid={`material-estimate-edit-toggle-${est.id}`} title="Edit">
            <Pencil size={11} className="text-[var(--fop-text-3)] hover:text-[var(--fop-primary)]" />
          </button>
          <button onClick={del} data-testid={`material-estimate-delete-${est.id}`} title="Delete">
            <Trash2 size={11} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" />
          </button>
        </div>
      </div>
      {est.stale_source && (
        <div className="inline-flex items-center gap-1 mt-0.5 text-[9px] text-[var(--fop-warning)]" data-testid={`material-estimate-stale-${est.id}`}>
          <AlertTriangle size={9} /> Source measurement is unverified/stale — recheck before ordering
        </div>
      )}
      {c ? (
        <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[var(--fop-text-2)]" data-testid={`material-estimate-computed-${est.id}`}>
          {est.shape === "rate" ? (
            <>
              <span>Measured quantity</span><span className="text-right">{fmt(c.measured_quantity)} {c.quantity_unit}</span>
              {c.waste_added > 0 && (<><span>+ Waste ({est.waste_pct}%)</span><span className="text-right">+{fmt(c.waste_added)} {c.quantity_unit}</span></>)}
              <span className="font-semibold text-[var(--fop-text)]">Order quantity</span><span className="text-right font-semibold text-[var(--fop-text)]">{fmt(c.order_quantity)} {c.quantity_unit}</span>
            </>
          ) : (
            <>
              <span>Design volume</span><span className="text-right">{fmt(c.measured_volume_cy)} cy</span>
              {c.waste_added_cy > 0 && (<><span>+ Waste ({est.waste_pct}%)</span><span className="text-right">+{fmt(c.waste_added_cy)} cy</span></>)}
              {c.compaction_added_cy > 0 && (<><span>+ Compaction ({est.compaction_pct}%)</span><span className="text-right">+{fmt(c.compaction_added_cy)} cy</span></>)}
              <span className="font-semibold text-[var(--fop-text)]">Order volume</span><span className="text-right font-semibold text-[var(--fop-text)]">{fmt(c.order_volume_cy)} cy</span>
              {c.mass_to_order != null && (<><span>To-order mass</span><span className="text-right">{fmt(c.mass_to_order)} {c.mass_unit}</span></>)}
            </>
          )}
          {c.truck_loads_to_order != null && (
            <><span className="inline-flex items-center gap-1"><Truck size={9} />Truck loads</span><span className="text-right">{c.truck_loads_to_order}</span></>
          )}
        </div>
      ) : (
        <div className="mt-1 text-[var(--st-escalated)]" data-testid={`material-estimate-calc-error-${est.id}`}>{est.calc_error || "Couldn't compute"}</div>
      )}
      {editing && (
        <EditEstimateForm
          est={est}
          onSaved={(updated) => { onUpdated(updated); setEditing(false); }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

export function MaterialEstimatePanel({ measurementId, measurementKind = "area" }) {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!measurementId) return;
    setLoading(true);
    api.get(`/measurements/${measurementId}/material-estimates`)
      .then((r) => setEstimates(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEstimates([]))
      .finally(() => setLoading(false));
  }, [measurementId]);

  return (
    <div data-testid="material-estimate-panel">
      {loading ? (
        <div className="text-[10px] text-[var(--fop-text-3)] font-mono">Loading…</div>
      ) : (
        <div className="space-y-1.5">
          {estimates.map((est) => (
            <EstimateRow
              key={est.id}
              est={est}
              onDeleted={(id) => setEstimates((prev) => prev.filter((e) => e.id !== id))}
              onUpdated={(updated) => setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))}
            />
          ))}
        </div>
      )}
      {showForm ? (
        <NewEstimateForm
          measurementId={measurementId}
          measurementKind={measurementKind}
          onCreated={(est) => { setEstimates((prev) => [est, ...prev]); setShowForm(false); }}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="mt-1.5 w-full flex items-center justify-center gap-1 px-2 py-1 rounded border border-dashed border-[var(--fop-border-hi)] text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] hover:border-[var(--fop-primary)]"
          data-testid="material-estimate-add-button"
        >
          <Plus size={11} /> Add material/volume estimate
        </button>
      )}
    </div>
  );
}

export default MaterialEstimatePanel;
